import { ChevronDown, ChevronUp, CornerDownLeft, FileText } from 'lucide-react'
import { Fragment, useMemo, useState } from 'react'
import { AssistantGlyph } from '@/components/brand/AssistantGlyph'
import { useAgentSidebar } from '@/components/layout/AgentSidebarContext'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { type LakeSearchScope, useLakeSearch } from '@/hooks/use-lake-search'
import type { ChunkHit, DocumentHit, LakeMatchMode, LakeMatchSource } from '@/services/api/lake'
import { cn } from '@/utils/cn'

interface LakeSearchComboboxProps {
  value: string
  onChange: (value: string) => void
  onSelectDocument: (documentId: string) => void
  onSelectChunk: (chunk: ChunkHit) => void
  scope?: LakeSearchScope
  placeholder?: string
}

const GROUP_PREVIEW_COUNT = 3

function GroupHeading({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-center justify-between px-3 pt-2 pb-2">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </span>
        <span className="rounded-sm bg-muted/50 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground">
          {count}
        </span>
      </div>
    </div>
  )
}

function GroupToggle({
  count,
  expanded,
  onToggle,
}: {
  count: number
  expanded: boolean
  onToggle: () => void
}) {
  if (count <= GROUP_PREVIEW_COUNT) return null

  return (
    <div className="px-3 pb-2 pt-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-full justify-center gap-1.5 rounded-md border border-border/60 text-[11px] text-muted-foreground hover:text-foreground"
        onClick={onToggle}
      >
        {expanded ? 'Show less' : `Show all (${count})`}
        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </Button>
    </div>
  )
}

function renderHtml(text: string | null | undefined) {
  return { __html: text ?? '' }
}

function ResultBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground bg-muted/70">
      {label}
    </span>
  )
}

function normalizeDisplayFilename(filename: string | null | undefined) {
  if (!filename) return 'Document'

  const match = filename.match(/^([a-f0-9-]{12,})_(.+)$/i)
  return match?.[2] ?? filename
}

function formatSourceLabel(source: LakeMatchSource) {
  if (source === 'path') return 'Path'
  if (source === 'summary') return 'Summary'
  return 'Content'
}

function formatModeLabel(mode: LakeMatchMode) {
  if (mode === 'semantic') return 'Semantic'
  if (mode === 'hybrid') return 'Hybrid'
  return 'Keyword'
}

function DocumentPath({ hit }: { hit: DocumentHit }) {
  const segments = hit.storage_segments?.length
    ? hit.storage_segments
    : (hit.storage_path?.split('/') ?? [])
  if (segments.length === 0) return null

  const normalizedSegments = [...segments]
  const lastIndex = normalizedSegments.length - 1
  normalizedSegments[lastIndex] = normalizeDisplayFilename(normalizedSegments[lastIndex])

  return (
    <div className="truncate text-[11px] text-muted-foreground [&_mark]:rounded-sm [&_mark]:bg-primary/20 [&_mark]:px-0.5 [&_mark]:text-foreground">
      {normalizedSegments.map((segment, index) => (
        <span key={`${segment}-${index}`}>
          {index > 0 && <span className="text-muted-foreground/50"> / </span>}
          <span dangerouslySetInnerHTML={renderHtml(segment)} />
        </span>
      ))}
    </div>
  )
}

function StoragePath({
  storagePath,
  storageSegments,
}: {
  storagePath: string | null
  storageSegments: string[]
}) {
  const segments = storageSegments.length > 0 ? storageSegments : (storagePath?.split('/') ?? [])
  if (segments.length === 0) return null

  const normalizedSegments = [...segments]
  const lastIndex = normalizedSegments.length - 1
  normalizedSegments[lastIndex] = normalizeDisplayFilename(normalizedSegments[lastIndex])

  return (
    <div className="truncate text-[11px] text-muted-foreground [&_mark]:rounded-sm [&_mark]:bg-primary/20 [&_mark]:px-0.5 [&_mark]:text-foreground">
      {normalizedSegments.map((segment, index) => (
        <span key={`${segment}-${index}`}>
          {index > 0 && <span className="text-muted-foreground/50"> / </span>}
          <span dangerouslySetInnerHTML={renderHtml(segment)} />
        </span>
      ))}
    </div>
  )
}

function DocumentSummary({ hit }: { hit: DocumentHit }) {
  const summary = hit.full_text_snippet || hit.file_summary
  if (!summary) return null

  return (
    <div
      className="line-clamp-2 text-[12px] leading-relaxed text-muted-foreground [&_mark]:rounded-sm [&_mark]:bg-primary/20 [&_mark]:px-0.5 [&_mark]:text-foreground"
      dangerouslySetInnerHTML={renderHtml(summary)}
    />
  )
}

function ProvenanceRow({ source, mode }: { source: LakeMatchSource; mode: LakeMatchMode }) {
  return (
    <div className="flex items-center gap-1.5">
      <ResultBadge label={formatSourceLabel(source)} />
      <ResultBadge label={formatModeLabel(mode)} />
    </div>
  )
}

/**
 * Inline trigger anchored to the right edge of the search input. Restrained
 * ghost affordance when empty; lifts into a primary-accented pill once the
 * user has typed something — signalling that the agent is ready to take the
 * current query as a starting prompt.
 */
function AskSpectreInlineTrigger({
  primed,
  onActivate,
}: {
  primed: boolean
  onActivate: () => void
}) {
  return (
    <button
      type="button"
      onClick={onActivate}
      aria-label="Ask XENDREL Agent"
      title={primed ? 'Ask XENDREL Agent about this' : 'Ask XENDREL Agent'}
      className={cn(
        'absolute top-1/2 right-1.5 z-10 flex h-7 -translate-y-1/2 items-center gap-1.5 rounded-md border px-2 text-[11.5px] font-medium transition-all duration-150 ease-out outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        primed
          ? 'border-primary/30 bg-primary/10 text-foreground shadow-[0_0_0_1px_var(--color-primary)/0.04,_0_4px_12px_-6px_var(--color-primary)/0.4] hover:border-primary/60 hover:bg-primary/15'
          : 'border-border/50 bg-card/60 text-muted-foreground hover:border-primary/40 hover:bg-accent hover:text-foreground'
      )}
    >
      {/* The glyph takes the trigger's own ink: muted at rest, foreground once
          hovered or primed — where it also keeps the copper glow. */}
      <AssistantGlyph
        ink="inherit"
        className={cn(
          'size-3.5 shrink-0',
          primed && 'drop-shadow-[0_0_4px_color-mix(in_oklab,var(--color-primary)_60%,transparent)]'
        )}
      />
      <span className="hidden sm:inline">Ask XENDREL</span>
      {primed && (
        <span className="hidden items-center gap-0.5 rounded border border-border/60 bg-muted/40 px-1 py-px font-mono text-[9.5px] uppercase tracking-wider text-muted-foreground sm:inline-flex">
          <CornerDownLeft className="size-2.5" />
        </span>
      )}
    </button>
  )
}

export function LakeSearchCombobox({
  value,
  onChange,
  onSelectDocument,
  onSelectChunk,
  scope,
  placeholder,
}: LakeSearchComboboxProps) {
  const [open, setOpen] = useState(false)
  const [documentsExpanded, setDocumentsExpanded] = useState(false)
  const [chunksExpanded, setChunksExpanded] = useState(false)
  const { data, isFetching, isError } = useLakeSearch(value, scope)
  const agent = useAgentSidebar()
  const hasDocuments = Boolean(data?.document_hits.length)
  const hasChunks = Boolean(data?.chunk_hits.length)
  const hasResults = hasDocuments || hasChunks
  const showPopover = open && value.trim().length >= 3
  const visibleDocuments = useMemo(
    () =>
      documentsExpanded
        ? (data?.document_hits ?? [])
        : (data?.document_hits ?? []).slice(0, GROUP_PREVIEW_COUNT),
    [data?.document_hits, documentsExpanded]
  )
  const visibleChunks = useMemo(
    () =>
      chunksExpanded
        ? (data?.chunk_hits ?? [])
        : (data?.chunk_hits ?? []).slice(0, GROUP_PREVIEW_COUNT),
    [chunksExpanded, data?.chunk_hits]
  )

  return (
    <Popover open={showPopover} onOpenChange={setOpen}>
      <Command
        shouldFilter={false}
        className="h-auto! w-full! overflow-visible rounded-lg! bg-transparent! p-0!"
        onKeyDown={event => {
          if (event.key === 'Escape') setOpen(false)
        }}
      >
        <PopoverAnchor asChild>
          <div className="relative w-full">
            <CommandInput
              value={value}
              onValueChange={nextValue => {
                onChange(nextValue)
                if (nextValue.trim().length >= 3) setOpen(true)
              }}
              onFocus={() => {
                if (value.trim().length >= 3) setOpen(true)
              }}
              onClick={() => {
                if (value.trim().length >= 3) setOpen(true)
              }}
              placeholder={placeholder ?? 'Search across lake documents or ask the XENDREL Agent'}
              // Phone: the input fills the whole box, so a tap anywhere in it focuses.
              className="pr-12 sm:pr-36 max-md:self-stretch"
              outerClassName="p-0"
              wrapperClassName="h-9! bg-card border-border focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/15 transition-colors"
            />
            <AskSpectreInlineTrigger
              primed={value.trim().length >= 3}
              onActivate={() => {
                agent.setOpen(true)
                setOpen(false)
              }}
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          sideOffset={8}
          className="z-[600] w-[min(var(--radix-popover-trigger-width),calc(100vw-2rem))] max-w-[960px] overflow-hidden rounded-xl p-0 shadow-xl max-md:mx-0"
          onOpenAutoFocus={event => event.preventDefault()}
        >
          <CommandList className="max-h-[min(28rem,calc(100vh-10rem))] overscroll-contain p-1.5">
            {isFetching && !data && (
              <div className="py-8 text-center text-xs text-muted-foreground/70">Searching…</div>
            )}
            {isError && (
              <CommandEmpty className="py-8 text-xs text-muted-foreground">
                Search failed. Please try again.
              </CommandEmpty>
            )}
            {!isFetching && !isError && data && !hasResults && (
              <CommandEmpty className="py-8 text-xs text-muted-foreground">
                No lake documents matched your search.
              </CommandEmpty>
            )}

            {hasDocuments && data && (
              <CommandGroup className="p-0">
                <GroupHeading label="Documents" count={data.document_hits.length} />
                <div className="px-2">
                  {visibleDocuments.map((hit, index) => (
                    <Fragment key={`document-${hit.document_id}`}>
                      {index > 0 && <CommandSeparator className="mx-2 my-0 bg-border/50" />}
                      <CommandItem
                        value={`document-${hit.document_id}`}
                        onSelect={() => {
                          onSelectDocument(hit.document_id)
                          setOpen(false)
                        }}
                        className="flex items-start gap-3 rounded-lg px-2.5 py-3 data-[selected=true]:bg-accent/50"
                      >
                        <FileText className="mt-[3px] h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <div className="flex min-w-0 flex-1 flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium">
                              {normalizeDisplayFilename(hit.filename)}
                            </span>
                          </div>
                          <ProvenanceRow source={hit.match_source} mode={hit.match_mode} />
                          <DocumentPath hit={hit} />
                          <DocumentSummary hit={hit} />
                        </div>
                      </CommandItem>
                    </Fragment>
                  ))}
                </div>
                <GroupToggle
                  count={data.document_hits.length}
                  expanded={documentsExpanded}
                  onToggle={() => setDocumentsExpanded(current => !current)}
                />
              </CommandGroup>
            )}

            {hasDocuments && hasChunks && <CommandSeparator className="mx-2.5 my-2 bg-border/40" />}

            {hasChunks && data && (
              <CommandGroup className="p-0">
                <GroupHeading label="Document content" count={data.chunk_hits.length} />
                <div className="px-2">
                  {visibleChunks.map((hit, index) => (
                    <Fragment key={`chunk-${hit.chunk_id}`}>
                      {index > 0 && <CommandSeparator className="mx-2 my-0 bg-border/50" />}
                      <CommandItem
                        value={`chunk-${hit.chunk_id}`}
                        onSelect={() => {
                          onSelectChunk(hit)
                          setOpen(false)
                        }}
                        className="flex items-start gap-3 rounded-lg px-2.5 py-3 data-[selected=true]:bg-accent/50"
                      >
                        <FileText className="mt-[3px] h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <div className="flex min-w-0 flex-1 flex-col gap-2">
                          <div className="flex items-start justify-between gap-3">
                            <span className="min-w-0 truncate text-sm font-medium">
                              {normalizeDisplayFilename(hit.filename)}
                            </span>
                            {(hit.section || hit.page_number) && (
                              <span className="flex shrink-0 items-center gap-1.5 pt-0.5 text-[10.5px] tabular-nums text-muted-foreground">
                                {hit.section && (
                                  <span className="max-w-[180px] truncate text-right max-md:hidden">
                                    {hit.section}
                                  </span>
                                )}
                                {hit.section && hit.page_number ? (
                                  <span className="opacity-40 max-md:hidden">·</span>
                                ) : null}
                                {hit.page_number && <span>p. {hit.page_number}</span>}
                              </span>
                            )}
                          </div>
                          <ProvenanceRow source={hit.match_source} mode={hit.match_mode} />
                          <StoragePath
                            storagePath={hit.storage_path}
                            storageSegments={hit.storage_segments}
                          />
                          <div
                            className="line-clamp-3 text-[13px] leading-relaxed text-muted-foreground [&_mark]:rounded-sm [&_mark]:bg-primary/20 [&_mark]:px-0.5 [&_mark]:text-foreground"
                            dangerouslySetInnerHTML={renderHtml(hit.content_snippet)}
                          />
                        </div>
                      </CommandItem>
                    </Fragment>
                  ))}
                </div>
                <GroupToggle
                  count={data.chunk_hits.length}
                  expanded={chunksExpanded}
                  onToggle={() => setChunksExpanded(current => !current)}
                />
              </CommandGroup>
            )}

            <CommandSeparator className="mx-2.5 my-2 bg-border/40" />
            <div className="px-1.5 pb-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  agent.setOpen(true)
                  setOpen(false)
                }}
                className="group flex h-9 w-full items-center justify-start gap-2 rounded-md text-xs font-medium text-primary hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
              >
                <AssistantGlyph className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1 truncate text-left">
                  {value.trim().length > 0 ? (
                    <>
                      Ask XENDREL Agent about{' '}
                      <span className="font-semibold">"{value.trim()}"</span>
                    </>
                  ) : (
                    <>Ask XENDREL Agent</>
                  )}
                </span>
                <span className="hidden text-[10px] uppercase tracking-wide text-muted-foreground group-hover:inline">
                  open chat
                </span>
              </Button>
            </div>
          </CommandList>
        </PopoverContent>
      </Command>
    </Popover>
  )
}
